import React from 'react'
import { motion } from 'motion/react'
import { ExhibitionPageDetailsFragment } from '@/gql/generated/graphql'
import { BodyText, DisplayText, HeadlineText, JumboText } from 'design-configuration/components'
import { exhibitionTransition } from '../[slug]/components/header'
import { getPrefixedLinkPath } from '@components/prefixed-link'
import { ThreeImageRow } from '../[slug]/components/three-image-row'
import { AboutCard } from '../[slug]/components/about-card'
import { iskraAnchor, iskraTitle, larsAnchor, larsTitle, zachAnchor, zachTitle } from './display'

export const GroupExhibitionQ42024AboutSection = ({
  exhibition
}: {
  exhibition: ExhibitionPageDetailsFragment
}): React.ReactElement => {
  return (
    <div className="w-full">
      {/* title and subtitle */}
      <motion.div
        className="px-md pt-xl pb-md lg:pb-xl w-full max-w-[1464px] md:w-[85%]"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={exhibitionTransition}
        viewport={{ once: true, amount: 0.5 }}
      >
        <JumboText className="font-medium">About</JumboText>
        <HeadlineText className="pt-xs">
          <span className="italic">Poetics of the Infinite</span> presents layered interpretations of the unknowable
          translated into scent, weaving, and sculptural form, featuring both physical and digital works by Iskra
          Velitchkova, Lars Wander, and Zach Lieberman.
        </HeadlineText>
      </motion.div>
      <div className="mb-[80px] flex w-full flex-col gap-y-[80px] lg:mb-[128px] lg:gap-y-[128px]">
        {/* cards */}
        <motion.div
          className="gap-x-sm gap-y-xl lg:gap-y-2xl relative grid w-full grid-cols-1 lg:grid-cols-3"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={exhibitionTransition}
          viewport={{ once: true, amount: 0.25 }}
        >
          {/* the three projects */}
          <AboutCard
            anchor={iskraAnchor}
            artist="Iskra Velitchkova"
            description={
              <div className="gap-y-xs text-on-surface-variant flex flex-col">
                <BodyText>
                  <span className="italic">INK (fragrances)</span>: Edition of 6 unique fragrances and vessels with
                  digital artworks
                </BodyText>
                <BodyText>
                  <span className="italic">INK</span>: 100 unique generative artworks
                </BodyText>
              </div>
            }
            title={iskraTitle}
            subtitle="Two separate collections"
            topImage={getPrefixedLinkPath('/images/poetics/about/about-Fragrance.jpg')}
            collection={exhibition.Collections?.[0]}
          />
          <AboutCard
            anchor={larsAnchor}
            artist="Lars Wander"
            description={
              <div className="gap-y-xs text-on-surface-variant flex flex-col">
                <BodyText>
                  <span className="italic">{larsTitle} (fabric)</span>: Single edition of 4 jacquard fabric panels with
                  digital artwork
                </BodyText>
                <BodyText>
                  <span className="italic">{larsTitle}</span>: 60 unique generative artworks
                </BodyText>
              </div>
            }
            title={larsTitle}
            subtitle="Two separate collections"
            topImage={getPrefixedLinkPath('/images/poetics/about/about-Curtains.jpg')}
            collection={exhibition.Collections?.[1]}
          />
          <AboutCard
            anchor={zachAnchor}
            artist="Zach Lieberman"
            description={
              <div className="gap-y-xs text-on-surface-variant flex flex-col">
                <BodyText>
                  <span className="italic">{zachTitle} (sculpture)</span>: Unique sculpture with digital artwork
                </BodyText>
                <BodyText>
                  <span className="italic">{zachTitle}</span>: 100 unique generative artworks
                </BodyText>
              </div>
            }
            title={zachTitle}
            subtitle="Two separate collections"
            topImage={getPrefixedLinkPath('/images/poetics/about/about-Sculpture.jpg')}
            collection={exhibition.Collections?.[2]}
          />
        </motion.div>
        {/* quote */}
        <motion.div
          className="px-md w-full max-w-[1464px] md:w-[85%]"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={exhibitionTransition}
          viewport={{ once: true, amount: 0.2 }}
        >
          <DisplayText className="font-light">
            Together, these bodies of work take nuanced lenses on confronting the unrepresentable. Whether through the
            nature of light, complex geometries, or scent, the artists’ desire to communicate what is ineffable about
            being human is revealed in glancing, glittering fragments.
          </DisplayText>
        </motion.div>
        {/* three image row */}
        <ThreeImageRow
          items={[
            { imageUrl: getPrefixedLinkPath('/images/poetics/visionRow/vision-1.jpg') },
            { imageUrl: getPrefixedLinkPath('/images/poetics/visionRow/vision-2.jpg') },
            { imageUrl: getPrefixedLinkPath('/images/poetics/visionRow/vision-3.jpg') }
          ]}
        />
      </div>
    </div>
  )
}
